import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, DialogActions, DialogContent } from '@mui/material';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useSnackbar } from 'notistack';
import { TCategoryFormData } from 'src/@types';
import { useDialogContext } from 'src/components/dialog/DialogContext';
import FormProvider from 'src/components/hook-form';
import useGetSelectOption from 'src/hooks/useGetSelectOption';
import { ICategory, useStores } from 'src/models';
import { categoryApi } from 'src/service';
import { useHandleResponse } from 'src/service/useHandleResponse';
import { FormType, TFormType } from 'src/static/constants/common/form';
import { getCategoryFormDefaultValues } from 'src/utils/getRHFDefaultValues';
import removeNullUndefined from 'src/utils/removeNullUndefined';

import { buildCategoryOptions } from '../utils/bulidCategoryOptions';
import { processImagePaths } from '../utils/processImagePath';

import { BasicInfo } from './section/BasicInfo';
import { ImagesInfo } from './section/ImagesInfo';
import { InfoByLang } from './section/InfoByLang';

export interface ImageState {
  thumbnail: File | string | null;
  desktop: File | string | null;
  mobile: File | string | null;
}
interface ICategoryFormDialogProps {
  formType: TFormType;
  onSubmitSuccess: () => void;
  selectedData?: ICategory;
}

export const CategoryFormDialog = observer(
  ({ formType, onSubmitSuccess, selectedData }: ICategoryFormDialogProps) => {
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const handleResponse = useHandleResponse();
    const { alertStore, categoryStore } = useStores();
    const { formId } = useDialogContext();
    const isCreate = formType === FormType.CREATE;
    const { getLang } = useGetSelectOption();
    const langList = getLang();
    // 썸네일, 데스크탑, 모바일 이미지 업로드
    const [images, setImages] = useState<ImageState>({
      thumbnail: null,
      desktop: null,
      mobile: null,
    });

    // 이미지 업로드 handler
    const handleImageChange = (type: keyof ImageState) => (file: File | string | null) => {
      setImages((prev) => ({ ...prev, [type]: file }));
    };

    const methods = useForm<TCategoryFormData>({
      mode: 'all',
      reValidateMode: 'onChange',
      // resolver: yupResolver(categoryFormValidationSchema),
      defaultValues: getCategoryFormDefaultValues(selectedData, langList),
    });

    const { reset, handleSubmit } = methods;

    // 3. 카테고리 옵션 (tree 형태)
    const categorySelectOptions = useMemo(
      () => [
        { value: 0, label: '없음' },
        ...buildCategoryOptions(toJS(categoryStore.categoryTree)),
      ],
      [categoryStore.categoryTree],
    );

    // 상세 조회 시 data set
    const initializeForm = useCallback(() => {
      if (selectedData) {
        setImages({
          thumbnail: selectedData.thumbnailPath ?? null,
          desktop: selectedData.bgDesktopPath ?? null,
          mobile: selectedData.bgMobilePath ?? null,
        });
        reset(getCategoryFormDefaultValues(selectedData, langList));
      }
    }, [selectedData, langList]);

    useEffect(() => {
      initializeForm();
    }, []);

    // 같은 부모 내 카테고리명 중복 체크 함수
    const checkNameDuplication = (categoryNm: string, psid: number | null, langCd: string) => {
      const checkInCategories = (categories: ICategory[]): boolean => {
        for (const cat of categories) {
          // 같은 언어의 카테고리명 찾기
          const sameLangCategory = cat.categoryInfoList?.find((info) => info.langCd === langCd);
          // 같은 부모, 같은 언어에서 이름 체크
          if (
            cat.categoryPsid === Number(psid) &&
            sameLangCategory?.categoryNm === categoryNm &&
            cat.categorySid !== selectedData?.categorySid
          ) {
            return true;
          }

          // 하위 카테고리 재귀적 체크
          if (cat.children && cat.children.length > 0) {
            if (checkInCategories(cat.children)) {
              return true;
            }
          }
        }
        return false;
      };

      return checkInCategories(categoryStore.categoryTree);
    };

    // 하나의 카테고리 아래 같은 이름의 카테고리 중복 체크
    const checkDuplication = (data: TCategoryFormData) => {
      // 모든 언어에 대해 중복 체크
      for (const categoryInfo of data.categoryInfoList) {
        const isDuplicate = checkNameDuplication(
          categoryInfo.categoryNm,
          data.categoryPsid,
          categoryInfo.langCd,
        );

        if (isDuplicate) {
          enqueueSnackbar(`같은 카테고리명 "${categoryInfo.categoryNm}"이(가) 이미 존재합니다.`, {
            variant: 'error',
          });
          return;
        }
      }
    };

    // 이미지 처리 (하나의 배열에서 drag drop으로 이미지 order 하기 위해 submit 시 분리)
    const processAllImages = (images: ImageState) => {
      const thumbnail = processImagePaths(
        images.thumbnail ? [images.thumbnail] : [],
        selectedData?.thumbnailPath ? [selectedData?.thumbnailPath] : [],
      );
      const desktop = processImagePaths(
        images.desktop ? [images.desktop] : [],
        selectedData?.bgDesktopPath ? [selectedData?.bgDesktopPath] : [],
      );
      const mobile = processImagePaths(
        images.mobile ? [images.mobile] : [],
        selectedData?.bgMobilePath ? [selectedData?.bgMobilePath] : [],
      );

      // 모든 삭제 경로를 하나의 배열로 합침
      const allDeletePaths = [
        ...thumbnail.deletePaths,
        ...desktop.deletePaths,
        ...mobile.deletePaths,
      ];

      return {
        ...(thumbnail.uploadFiles.length > 0 && { thumbnail: thumbnail.uploadFiles[0] }),
        ...(thumbnail.existingPaths.length > 0 && { thumbnailPath: thumbnail.existingPaths[0] }),
        ...(desktop.uploadFiles.length > 0 && { desktopImageFile: desktop.uploadFiles[0] }),
        ...(desktop.existingPaths.length > 0 && { bgDesktopPath: desktop.existingPaths[0] }),
        ...(mobile.uploadFiles.length > 0 && { mobileImageFile: mobile.uploadFiles[0] }),
        ...(mobile.existingPaths.length > 0 && { bgMobilePath: mobile.existingPaths[0] }),
        // 삭제할 경로는 1개 배열로 전달
        ...(allDeletePaths.length > 0 && { deleteFilePaths: allDeletePaths }),
      };
    };

    const onValid = async (data: TCategoryFormData) => {
      // 모든 언어에 대해 중복 체크
      await checkDuplication(data);

      const formData = {
        ...removeNullUndefined(data),
        categoryPsid: data.categoryPsid,
        categoryInfoList: data.categoryInfoList,
        ctgyDpTypeCd: data.ctgyDpTypeCd,
        // 썸네일, 데스크탑, 모바일 이미지 처리
        ...processAllImages(images),
      };

      const responseText = isCreate ? '등록' : '수정';
      const result = isCreate
        ? await categoryApi.post(formData)
        : await categoryApi.put(selectedData?.categorySid || 0, formData);

      // 결과 처리
      if (await result?.success) {
        handleResponse(
          `${responseText}이 완료되었습니다.`,
          `${responseText}이 실패되었습니다.`,
          onSubmitSuccess,
        );
      } else {
        console.error(`${responseText} 실패`);
      }
    };

    // 삭제
    const handleDelete = () => {
      alertStore.setOnConfirm(async () => {
        categoryApi
          .remove(selectedData?.categorySid)
          .then(() =>
            handleResponse(
              '카테고리 삭제가 완료되었습니다.',
              '카테고리 삭제가 실패되었습니다.',
              onSubmitSuccess,
            ),
          );
      });
      alertStore.setProps({
        open: true,
        title: '카테고리 삭제',
        content: '해당 카테고리을 삭제하시겠습니까?',
        category: 'error',
        hasCancelButton: true,
      });
    };

    return (
      <FormProvider methods={methods} onSubmit={handleSubmit(onValid)} formId={formId}>
        <DialogContent dividers sx={{ pt: '10px' }}>
          {/* 기본 정보 */}
          <BasicInfo categorySelectOptions={categorySelectOptions} />
          {/* 언어별 필드 */}
          <InfoByLang formType={formType} selectedData={selectedData} />
          {/* 썸네일, 데스크탑, 모바일 이미지 업로드 */}
          <ImagesInfo images={images} handleImageChange={handleImageChange} />
        </DialogContent>
        <DialogActions>
          <>
            {formType === FormType.UPDATE && (
              <Button onClick={handleDelete} variant="contained" color="error">
                {t('common.button.delete')}
              </Button>
            )}
            <Button type="submit" variant="contained" form={formId} color="primary">
              {formType === FormType.CREATE ? t('common.button.register') : t('common.button.edit')}
            </Button>
          </>
        </DialogActions>
      </FormProvider>
    );
  },
);
